import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Paper,
  Grid,
  Typography,
  Button,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Stack,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  IconButton,
  Snackbar,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Icon } from "@iconify/react";
import { DataGrid } from "@mui/x-data-grid";
import { useSelectedTeam, useSelectedTeamState } from "../hooks/useSelectedTeam";
import { useAccess } from "../hooks/useAccess.hook";
import { FEATURE } from "../util/enums";
import {
  rfidUsageReportUrl,
  rfidUsageReportEmailUrl,
  teamExcelDownloadUrl,
} from "src/react-query/endPoints";
import Token from "src/util/Token";
import RFIDTagTransactionDetails from "./RFIDTagTransactionDetails";
import { BaseTable } from "src/components/BaseTable/BaseTable";
import { useGetDetail } from "src/hooks/useFetch.hook";
import { Context } from "src/context";
import { toLocaleStringInTimezone } from "src/utils/timezoneFormat";

export default function RFIDUsageReports() {
  const theme = useTheme();
  const { timezone } = useContext(Context);
  const { teamId } = useSelectedTeam();
  const { teamCurrencySymbol } = useSelectedTeamState();
  const hasReadAccess = useAccess(FEATURE.RFID_USAGE_REPORTS, "R");
  
  const { data: profileData, isFetching: profileLoading } = useGetDetail("profile", { id: 0 });
  
  const [dateRange, setDateRange] = useState("all");
  const [tagType, setTagType] = useState("all");
  const [usagePattern, setUsagePattern] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedRFID, setSelectedRFID] = useState(null);
  const [error, setError] = useState(null);
  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState({
    totalTags: 0,
    totalEnergy: 0,
    totalRevenue: 0,
    flaggedTags: 0,
    totalTransactions: 0,
  });
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 10,
    total: 0,
  });
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const brandGradient = theme.palette.gradients.brand;
  const brandShadow = theme.palette.mode === "dark"
    ? "0 22px 50px rgba(2,12,27,0.55)"
    : "0 18px 40px rgba(89,118,255,0.22)";
  const gradientIcon = (iconName, tone = "brand") => {
    if (tone === "warning") {
      return (
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: alpha(theme.palette.warning.main, 0.18),
            color: theme.palette.warning.dark,
          }}
        >
          <Icon icon={iconName} width={22} height={22} />
        </Box>
      );
    }
    return (
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundImage: brandGradient,
          color: theme.palette.primary.contrastText,
          boxShadow: brandShadow,
        }}
      >
        <Icon icon={iconName} width={22} height={22} />
      </Box>
    );
  };

  const headerBtnStyle = {
    fontWeight: 600,
    textAlign: "center",
    justifyContent: "center",
    borderRadius: 2,
    boxShadow: 1,
    px: 2,
    py: 1.2,
    display: "flex",
    alignItems: "center",
    transition: "all 0.2s cubic-bezier(.4,0,.2,1)",
    width: "100%",
    "&:hover": {
      transform: "translateY(-3px)",
      boxShadow: 4,
    },
  };

  const handleViewDetails = (data) => {
    const row = data.row || data;
    setSelectedRFID(row.rfid);
    setDetailsOpen(true);
  };

  const columns = [
    { field: "rfid", headerName: "RFID", flex: 1, minWidth: 120 },
    { field: "email", headerName: "Email ID", flex: 1, minWidth: 200 },
    { field: "tagType", headerName: "Tag Type", flex: 1, minWidth: 100 },
    {
      field: "transactions",
      headerName: "Transactions",
      flex: 1,
      type: "number",
      minWidth: 120,
    },
    {
      field: "energy",
      headerName: "Energy (kWh)",
      flex: 1,
      type: "number",
      minWidth: 130,
    },
    {
      field: "amount",
      headerName: `Amount (${teamCurrencySymbol})`,
      flex: 1,
      type: "number",
      minWidth: 120,
    },
    {
      field: "avgDuration",
      headerName: "Avg Duration (min)",
      flex: 1,
      type: "number",
      minWidth: 150,
    },
    {
      field: "peakUsage",
      headerName: "Peak Usage (kWh)",
      flex: 1,
      type: "number",
      minWidth: 150,
    },
    {
      field: "lastUsed",
      headerName: "Last Used",
      flex: 1,
      minWidth: 180,
      valueFormatter: (params) => {
        if (!params.value) return "Never";
        return timezone.timezone?.value
          ? toLocaleStringInTimezone(params.value, timezone.timezone.value)
          : new Date(params.value).toLocaleString();
      },
    },
    { field: "pattern", headerName: "Pattern", flex: 1, minWidth: 100 },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={
            params.value === "Active"
              ? "success"
              : params.value === "Blocked"
              ? "error"
              : params.value === "Disabled"
              ? "warning"
              : "default"
          }
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="View Details">
          <IconButton
            size="small"
            onClick={() => handleViewDetails(params.row)}
          >
            <Icon icon="eva:eye-fill" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const fetchReports = async () => {
    if (!teamId) return;
    setLoading(true);
    setError(null);
    const token = Token.get();

    const queryParams = new URLSearchParams({
      page: pagination.page + 1,
      limit: pagination.pageSize,
      search: search || "",
      tagType: tagType || "all",
      usagePattern: usagePattern || "all",
      dateRange: dateRange || "last30",
    });

    if (dateRange === "custom" && from && to) {
      queryParams.append("from", from);
      queryParams.append("to", to);
    }
    if (timezone.timezone?.value) {
      queryParams.append("tz", timezone.timezone.value);
    }

    const url = `${rfidUsageReportUrl(teamId)}?${queryParams}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setReports(
          (data.data || []).map((row, index) => ({
            id: row.rfid || row.id || index,
            ...row,
          }))
        );
        setSummary({
          totalTransactions: Number(data.kpis?.totalTransactions || 0),
          totalEnergy: Number(data.kpis?.totalEnergy || 0),
          totalAmount: Number(data.kpis?.totalRevenue || 0),
          flaggedTags: Number(data.kpis?.flaggedTags || 0),
          totalTags: Number(data.summary?.totalTags || 0),
        });
        setPagination((prev) => ({
          ...prev,
          total: data.pagination?.total || prev.total,
        }));
      } else {
        setError(data.message || "Failed to fetch RFID usage reports");
      }
    } catch (error) {
      setError(error.message || "Failed to fetch RFID usage reports");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailReport = async () => {
    if (!teamId) return;
    
    const userEmail = profileData?.email;
    
    if (!userEmail) {
      setError("No email address found in your profile. Please update your profile with a valid email address.");
      return;
    }
    
    setEmailLoading(true);
    setError(null);

    const token = Token.get();

    try {
      const response = await fetch(rfidUsageReportEmailUrl(teamId), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: userEmail,
          dateRange,
          from,
          to,
          tagType,
          usagePattern,
          search,
          tz: timezone.timezone?.value,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccessMessage(`Report has been sent to ${userEmail}!`);
        console.log("Report has been sent to your email!");
      } else {
        setError(data.message || "Failed to send report email");
      }
    } catch (error) {
      setError(error.message || "Failed to send report email");
    } finally {
      setEmailLoading(false);
    }
  };

  const handleExcelDownload = async () => {
    setExcelLoading(true);
    setError(null);
    const token = Token.get();

    try {
      const queryParams = new URLSearchParams({
        page: 1,
        limit: 999999,
        search: search || "",
        tagType: tagType || "all",
        usagePattern: usagePattern || "all",
        dateRange: dateRange || "last30",
      });

      if (dateRange === "custom" && from && to) {
        queryParams.append("from", from);
        queryParams.append("to", to);
      }
      if (timezone.timezone?.value) {
        queryParams.append("tz", timezone.timezone.value);
      }

      const fetchUrl = `${rfidUsageReportUrl(teamId)}?${queryParams}`;
      
      const fetchResponse = await fetch(fetchUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!fetchResponse.ok) {
        throw new Error(`HTTP error! status: ${fetchResponse.status}`);
      }

      const fetchData = await fetchResponse.json();
      
      if (!fetchData.success || !fetchData.data?.length) {
        setError("No data available to download");
        return;
      }

      const excelData = fetchData.data.map(row => ({
        RFID: row.rfid,
        "Email ID": row.email || "",
        "Tag Type": row.tagType,
        Transactions: row.transactions,
        "Energy (kWh)": row.energy,
        [`Amount (${teamCurrencySymbol})`]: row.amount,
        "Avg Duration (min)": row.avgDuration,
        "Peak Usage (kWh)": row.peakUsage,
        "Last Used": row.lastUsed
          ? (timezone.timezone?.value
              ? toLocaleStringInTimezone(row.lastUsed, timezone.timezone.value)
              : new Date(row.lastUsed).toLocaleString())
          : "Never",
        Pattern: row.pattern,
        Status: row.status
      }));

      const response = await fetch(teamExcelDownloadUrl(teamId), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          data: excelData,
          filename: "rfid-usage-report"
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the filename from the response headers
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]
        : 'rfid-usage-report.xlsx';

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSuccessMessage(`Excel report downloaded successfully! Total records: ${excelData.length}`);
    } catch (error) {
      setError(error.message || "Failed to download Excel report");
    } finally {
      setExcelLoading(false);
    }
  };

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      page: 0,
      pageSize: prev.pageSize,
    }));
  }, [dateRange, tagType, usagePattern, search, teamId]);

  useEffect(() => {
    fetchReports();
  }, [
    pagination.page,
    pagination.pageSize,
    dateRange,
    from,
    to,
    tagType,
    usagePattern,
    search,
    teamId,
    timezone.timezone?.value,
  ]);

  const getSubtitle = () => {
    if (dateRange === "last7") return "Last 7 days";
    if (dateRange === "last30") return "Last 30 days";
    if (dateRange === "custom" && from && to) {
      // from/to are plain "YYYY-MM-DD" strings the user picked, unrelated to
      // any timezone — display the digits as-is rather than routing them
      // through a Date object (which would parse as UTC midnight and can
      // shift the displayed day depending on the browser's offset).
      return `From ${from} to ${to}`;
    }
    return "All Time";
  };

  const summaryStats = [
    {
      title: "Total Transactions",
      value: summary.totalTransactions,
      subtitle: getSubtitle(),
      icon: gradientIcon("eva:filter-fill"),
    },
    {
      title: "Total Energy",
      value: `${summary.totalEnergy?.toFixed(2) || "0.00"} kWh`,
      subtitle: getSubtitle(),
      icon: gradientIcon("eva:flash-fill"),
    },
    {
      title: "Total Revenue",
      value: `${teamCurrencySymbol}${summary.totalAmount?.toFixed(2) || "0.00"}`,
      subtitle: getSubtitle(),
      icon: gradientIcon("eva:credit-card-fill"),
    },
    {
      title: "Flagged Tags",
      value: summary.flaggedTags,
      icon: gradientIcon("eva:alert-triangle-fill", "warning"),
    },
  ];

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  return (
    <Box
      sx={{ background: "#f9fafb", minHeight: "100vh", p: { xs: 1, md: 3 } }}
    >
      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSuccessMessage("")} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} mb={3}>
        {summaryStats.map((stat) => (
          <Grid item xs={12} sm={6} md={3} key={stat.title}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                minHeight: 120,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? alpha(theme.palette.background.paper, 0.4)
                    : alpha(theme.palette.common.white, 0.92),
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 10px 30px rgba(2,12,27,0.45)"
                    : "0 15px 40px rgba(15,23,42,0.08)",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                {stat.icon}
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {stat.title}
                </Typography>
              </Stack>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {stat.value}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stat.subtitle}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        RFID Usage Reports
      </Typography>

      <Paper elevation={2} sx={{ borderRadius: 3, p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateRange}
                label="Date Range"
                onChange={(e) => setDateRange(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="last7">Last 7 days</MenuItem>
                <MenuItem value="last30">Last 30 days</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {dateRange === "custom" && (
            <>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="From"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={from || ""}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="To"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={to || ""}
                  onChange={(e) => setTo(e.target.value)}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Tag Type</InputLabel>
              <Select
                value={tagType}
                label="Tag Type"
                onChange={(e) => setTagType(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="RFID">RFID</MenuItem>
                <MenuItem value="PREPAID">Prepaid</MenuItem>
                <MenuItem value="CARD">Card</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Usage Pattern</InputLabel>
              <Select
                value={usagePattern}
                label="Usage Pattern"
                onChange={(e) => setUsagePattern(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="No Usage">No Usage</MenuItem>
                <MenuItem value="Heavy">Heavy</MenuItem>
                <MenuItem value="Moderate">Moderate</MenuItem>
                <MenuItem value="Light">Light</MenuItem>
                <MenuItem value="Occasional">Occasional</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems="stretch"
              sx={{ width: "100%" }}
            >
              <TextField
                size="small"
                fullWidth
                placeholder="Search RFID"
                value={search}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <Icon
                      icon="eva:search-fill"
                      color="#637381"
                      width={22}
                      height={22}
                      style={{ marginRight: 8 }}
                    />
                  ),
                }}
              />
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{
                  width: "100%",
                  alignItems: { xs: "stretch", sm: "center" },
                }}
              >
                <Button
                  variant="contained"
                  color="success"
                  startIcon={
                    excelLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <Icon icon="eva:download-fill" width={20} height={20} />
                    )
                  }
                  sx={{
                    ...headerBtnStyle,
                    py: 1,
                    fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { xs: "100%", sm: "140px" },
                    maxWidth: { sm: "180px" },
                  }}
                  onClick={handleExcelDownload}
                  disabled={excelLoading || !teamId}
                >
                  {excelLoading ? "Downloading..." : "Download Excel"}
                </Button>
                {/* <Tooltip 
                  title={profileData?.email ? `Send to: ${profileData.email}` : "Loading user email..."}
                >
                  <span> 
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={
                        emailLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <Icon icon="eva:email-fill" width={20} height={20} />
                        )
                      }
                      sx={{
                        ...headerBtnStyle,
                        py: 1,
                        fontSize: { xs: "0.75rem", sm: "0.85rem" },
                        width: { xs: "100%", sm: "auto" },
                        minWidth: { xs: "100%", sm: "140px" },
                        maxWidth: { sm: "180px" },
                      }}
                      onClick={handleEmailReport}
                      disabled={emailLoading || profileLoading || !profileData?.email}
                    >
                      {emailLoading ? "Sending..." : "Email Report"}
                    </Button>
                  </span>
                </Tooltip> */}
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Table */}
      <Box sx={{ overflowX: "auto" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          RFID Usage Details
        </Typography>
        <Paper elevation={2} sx={{ borderRadius: 3, p: 2 }}>
          <BaseTable
            rows={reports}
            columns={columns}
            page={pagination.page}
            pageSize={pagination.pageSize}
            rowCount={pagination.total}
            onPageChange={(newPage) =>
              setPagination((prev) => ({
                ...prev,
                page: newPage,
              }))
            }
            onPageSizeChange={(newPageSize) =>
              setPagination((prev) => ({
                ...prev,
                pageSize: newPageSize,
                page: 0,
              }))
            }
            rowOrCellClickRedirect={handleViewDetails}
            serverRowFetcher
            loading={loading}
          />
        </Paper>
        <RFIDTagTransactionDetails
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          rfid={selectedRFID}
        />
      </Box>
    </Box>
  );
}